import React from 'react';
import ReactDOM from 'react-dom/client';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import './index.css';
import { AuthProvider } from './context/authContext';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});
import {
  Login,
  NotFound,
  AdminWrapper,
  Dashboard,
  MinisterListPage,
  MinisterDetailPage,
  CredentialRequestList,
  FunctionCredentialRequestList,
  CredentialRequestDetail,
  ReportsLandingPage,
  MinistersListReportPage,
  MinisterRegistryReportPage,
  MinisterTransfersReportPage,
  MinisterDegreeChangesReportPage,
  InstitutionLandingPage,
  EventListPage,
  EventFormPage,
  EventDetailPage,
} from './pages';
import TerritoryLayout from './pages/sections/territories/TerritoryLayout';
import InformationTab from './pages/sections/territories/InformationTab';
import MinistersTab from './pages/sections/territories/MinistersTab';
import ChurchesTab from './pages/sections/territories/ChurchesTab';
import CredentialsTab from './pages/sections/territories/CredentialsTab';
import HistorialTab from './pages/sections/territories/HistorialTab';
import DirectorioNacionalLayout from './pages/sections/territories/DirectorioNacionalLayout';
import NacionalInformationTab from './pages/sections/territories/NacionalInformationTab';
import NacionalHistorialTab from './pages/sections/territories/NacionalHistorialTab';
import NacionalMembersTab from './pages/sections/territories/NacionalMembersTab';
import NacionalCredentialsTab from './pages/sections/territories/NacionalCredentialsTab';
import { TithesPage } from './features/tithes/pages/TithesPage';
import { SettingsPage } from './features/settings/pages/SettingsPage';
import { ReceiptsListPage } from './features/receipts/pages/ReceiptsListPage';
import { PublicReceiptPage } from './features/receipts/pages/PublicReceiptPage';
import { PublicRegistrationPage } from './features/events/public/pages/PublicRegistrationPage';
import { PublicRegistrationViewPage } from './features/events/public/pages/PublicRegistrationViewPage';
import { PublicRegistrationEditPage } from './features/events/public/pages/PublicRegistrationEditPage';
import { OpenEventsPage } from './features/events/public/pages/OpenEventsPage';
import { isRegistrationHost } from './features/events/public/publicUrls';
import { EventCheckinPage } from './features/events/checkin/pages/EventCheckinPage';
import { KioskRedirect } from './features/events/checkin/pages/KioskRedirect';
import { RegistrationDeskPage } from './features/events/desk/pages/RegistrationDeskPage';
import { VotingListPage, ElectionDetailPage, KioskVotePage, PublicVotePage, ProjectionPage, ActaPage, CheckinPage, CheckinListPage } from './features/voting';
import { RoleListPage, RoleDetailPage } from './features/roles';
import { UsersPage } from './features/users';
import { MyAccountPage } from './features/account';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { Toaster } from '@/components/ui/toaster';
import './test-supabase';

// inscripcion.addbolivia.org serves only the public registration pages, with short paths
const registrationHostRouter = () =>
  createBrowserRouter([
    { path: '/', element: <OpenEventsPage />, errorElement: <NotFound /> },
    { path: '/p/:token', element: <PublicRegistrationViewPage />, errorElement: <NotFound /> },
    { path: '/editar/:token', element: <PublicRegistrationEditPage />, errorElement: <NotFound /> },
    { path: '/r/:token', element: <PublicReceiptPage />, errorElement: <NotFound /> },
    { path: '/v/:electionId', element: <PublicVotePage />, errorElement: <NotFound /> },
    { path: '/inscripcion/:code', element: <PublicRegistrationPage />, errorElement: <NotFound /> },
    { path: '/:code', element: <PublicRegistrationPage />, errorElement: <NotFound /> },
    { path: '*', element: <NotFound /> },
  ]);

const adminRouter = () => createBrowserRouter([
  {
    path: '/login',
    element: <Login />,
    errorElement: <NotFound />,
  },
  {
    // Public, unauthenticated — reached by scanning a receipt's QR code.
    path: '/r/:token',
    element: <PublicReceiptPage />,
    errorElement: <NotFound />,
  },
  {
    // Public, unauthenticated — events open for registration.
    path: '/inscripcion',
    element: <OpenEventsPage />,
    errorElement: <NotFound />,
  },
  {
    // Public, unauthenticated — event pre-registration form.
    path: '/inscripcion/:code',
    element: <PublicRegistrationPage />,
    errorElement: <NotFound />,
  },
  {
    // Public, unauthenticated — badge/receipt QR target: the filled registration form.
    path: '/p/:token',
    element: <PublicRegistrationViewPage />,
    errorElement: <NotFound />,
  },
  {
    // Public, unauthenticated — personal link an admin sends so a participant completes their form (ADB-169).
    path: '/editar/:token',
    element: <PublicRegistrationEditPage />,
    errorElement: <NotFound />,
  },
  {
    // Event-day check-in for ushers (auth required inside; no admin sidebar)
    path: '/checkin/:eventId',
    element: <EventCheckinPage />,
    errorElement: <NotFound />,
  },
  {
    // On-site registration desk: register, charge, receipt and credential (auth required inside)
    path: '/registro/:eventId',
    element: <RegistrationDeskPage />,
    errorElement: <NotFound />,
  },
  {
    // Public voting page, opened from the QR on the screen (no login)
    path: '/v/:electionId',
    element: <PublicVotePage />,
    errorElement: <NotFound />,
  },
  {
    // Big-screen projection of an election (auth + election.view checked inside; no sidebar)
    path: '/proyeccion/:electionId',
    element: <ProjectionPage />,
    errorElement: <NotFound />,
  },
  {
    // Printable results sheet
    path: '/acta/:electionId',
    element: <ActaPage />,
    errorElement: <NotFound />,
  },
  {
    // Voting kiosk: a volunteer votes on behalf of someone (auth + vote.proxy checked inside; no sidebar)
    path: '/votar/:electionId',
    element: <KioskVotePage />,
    errorElement: <NotFound />,
  },
  {
    path: '/kiosk/:eventId/:sessionId',
    element: <KioskRedirect />,
    errorElement: <NotFound />,
  },
  {
    path: '/',
    element: <AdminWrapper />,
    children: [
      {
        index: true,
        element: <Dashboard />,
      },
      {
        path: 'ministers',
        element: <ProtectedRoute perms={['minister.view']}><MinisterListPage /></ProtectedRoute>,
      },
      {
        path: 'ministers/:id',
        element: <ProtectedRoute perms={['minister.view']}><MinisterDetailPage /></ProtectedRoute>,
      },
      {
        path: 'credential-requests',
        element: <ProtectedRoute perms={['credential_request.view']}><CredentialRequestList /></ProtectedRoute>,
      },
      {
        path: 'credential-requests/function',
        element: <ProtectedRoute perms={['credential_request.view']}><FunctionCredentialRequestList /></ProtectedRoute>,
      },
      {
        path: 'credential-requests/function/:id',
        element: <ProtectedRoute perms={['credential_request.view']}><CredentialRequestDetail /></ProtectedRoute>,
      },
      {
        path: 'credential-requests/:id',
        element: <ProtectedRoute perms={['credential_request.view']}><CredentialRequestDetail /></ProtectedRoute>,
      },
      // Reports routes
      {
        path: 'reports',
        children: [
          {
            index: true,
            element: <ProtectedRoute perms={['report.view']}><ReportsLandingPage /></ProtectedRoute>,
          },
          {
            path: 'credentials/ministers-list',
            element: <ProtectedRoute perms={['report.view']}><MinistersListReportPage /></ProtectedRoute>,
          },
          {
            path: 'ministers/registry',
            element: <ProtectedRoute perms={['report.view']}><MinisterRegistryReportPage /></ProtectedRoute>,
          },
          {
            path: 'ministers/transfers',
            element: <ProtectedRoute perms={['report.view']}><MinisterTransfersReportPage /></ProtectedRoute>,
          },
          {
            path: 'ministers/degree-changes',
            element: <ProtectedRoute perms={['report.view']}><MinisterDegreeChangesReportPage /></ProtectedRoute>,
          },
        ],
      },
      // Institution routes
      {
        path: 'institutions/:slug',
        element: <ProtectedRoute perms={['institution.view']}><InstitutionLandingPage /></ProtectedRoute>,
      },
      // Tithes
      {
        path: 'tithes',
        element: <ProtectedRoute perms={['tithe.view', 'tithe.view_own']}><TithesPage /></ProtectedRoute>,
      },
      // Receipts
      {
        path: 'receipts',
        element: <ProtectedRoute perms={['receipt.view']}><ReceiptsListPage /></ProtectedRoute>,
      },
      // Settings (admin)
      {
        path: 'settings',
        element: <ProtectedRoute perms={['bank_account.view']}><SettingsPage /></ProtectedRoute>,
      },
      // Users and own account
      {
        path: 'users',
        element: <ProtectedRoute perms={['user.manage']}><UsersPage /></ProtectedRoute>,
      },
      {
        path: 'cuenta',
        element: <MyAccountPage />,
      },
      // Roles & permissions
      {
        path: 'roles',
        element: <ProtectedRoute perms={['role.manage']}><RoleListPage /></ProtectedRoute>,
      },
      {
        path: 'roles/:id',
        element: <ProtectedRoute perms={['role.manage']}><RoleDetailPage /></ProtectedRoute>,
      },
      // Event routes
      {
        path: 'events',
        element: <ProtectedRoute perms={['event.view']}><EventListPage /></ProtectedRoute>,
      },
      {
        path: 'events/new',
        element: <ProtectedRoute perms={['event.create']}><EventFormPage /></ProtectedRoute>,
      },
      {
        path: 'events/:id',
        element: <ProtectedRoute perms={['event.view']}><EventDetailPage /></ProtectedRoute>,
      },
      {
        path: 'events/:id/edit',
        element: <ProtectedRoute perms={['event.edit']}><EventFormPage /></ProtectedRoute>,
      },
      // Voting (access follows the election.* permissions)
      {
        path: 'voting',
        element: <ProtectedRoute perms={['election.view']}><VotingListPage /></ProtectedRoute>,
      },
      {
        path: 'voting/:electionId',
        element: <ProtectedRoute perms={['election.view']}><ElectionDetailPage /></ProtectedRoute>,
      },
      // Voting check-in for ushers (phone camera); needs only election.checkin
      {
        path: 'voting/checkin',
        element: <ProtectedRoute perms={['election.checkin']}><CheckinListPage /></ProtectedRoute>,
      },
      {
        path: 'voting/:electionId/checkin',
        element: <ProtectedRoute perms={['election.checkin']}><CheckinPage /></ProtectedRoute>,
      },
      // Territory routes - Districts
      {
        path: 'territories/districts/:districtId',
        element: <TerritoryLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="information" replace />,
          },
          {
            path: 'information',
            element: <InformationTab />,
          },
          {
            path: 'ministers',
            element: <MinistersTab />,
          },
          {
            path: 'churches',
            element: <ChurchesTab />,
          },
          {
            path: 'credentials',
            element: <CredentialsTab />,
          },
          {
            path: 'historial',
            element: <HistorialTab />,
          },
        ],
      },
      // Directorio Nacional
      {
        path: 'directorio-nacional',
        element: <DirectorioNacionalLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="information" replace />,
          },
          {
            path: 'information',
            element: <NacionalInformationTab />,
          },
          {
            path: 'miembros',
            element: <NacionalMembersTab />,
          },
          {
            path: 'credenciales',
            element: <NacionalCredentialsTab />,
          },
          {
            path: 'historial',
            element: <NacionalHistorialTab />,
          },
        ],
      },
      // Territory routes - Regions
      {
        path: 'territories/regions/:regionId',
        element: <TerritoryLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="information" replace />,
          },
          {
            path: 'information',
            element: <InformationTab />,
          },
          {
            path: 'ministers',
            element: <MinistersTab />,
          },
          {
            path: 'churches',
            element: <ChurchesTab />,
          },
          {
            path: 'credentials',
            element: <CredentialsTab />,
          },
          {
            path: 'historial',
            element: <HistorialTab />,
          },
        ],
      },
    ],
  },
]);

const router = isRegistrationHost() ? registrationHostRouter() : adminRouter();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <RouterProvider router={router} />
        <Toaster />
      </AuthProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
